import { useId } from 'react'

import { CheckboxField, SelectField } from '@krp/ui-kit'

import {
  ONEC_CREATION_DEFAULTS,
  ONEC_PROFILES,
  ONEC_TEMPLATE_KEYS,
  canonicalUrl,
  stringField,
  type OneCSettingsIssue,
} from '../model/settings'
import '../../../shared/ui/onec.css'

export interface OneCConnectionFormProps {
  settings: Readonly<Record<string, unknown>>
  onChange: (settings: Record<string, unknown>) => void
  disabled: boolean
  issues: readonly OneCSettingsIssue[]
}

function Field({
  label,
  value,
  onChange,
  disabled,
  issue,
  type = 'text',
  hint,
  onBlur,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
  issue: OneCSettingsIssue | undefined
  type?: 'text' | 'url'
  hint?: string
  onBlur?: () => void
}) {
  const id = useId()
  return (
    <div className="krp-field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={issue !== undefined || undefined}
        aria-describedby={issue !== undefined ? `${id}-issue` : undefined}
        onChange={(event) => onChange(event.target.value)}
        {...(onBlur !== undefined ? { onBlur } : {})}
      />
      {hint !== undefined && <small>{hint}</small>}
      {issue !== undefined && (
        <small id={`${id}-issue`} role="alert">
          {issue.message}
        </small>
      )}
    </div>
  )
}

/**
 * Настройки подключения 1С (форма пакета вида для реестра Platform). Только
 * значение и колбэк: store, запросов и пароля здесь нет — пароль вводится
 * общим полем секрета формы ресурса и в настройки не попадает.
 */
export function OneCConnectionForm({ settings, onChange, disabled, issues }: OneCConnectionFormProps) {
  const issueOf = (field: string) => issues.find((issue) => issue.field === field)
  const set = (name: string, value: unknown) => onChange({ ...settings, [name]: value })
  const template =
    typeof settings.creationTemplate === 'object' && settings.creationTemplate !== null
      ? (settings.creationTemplate as Record<string, unknown>)
      : null
  const setTemplate = (key: string, value: string) => set('creationTemplate', { ...(template ?? {}), [key]: value })
  const defaults =
    typeof settings.creationDefaults === 'object' && settings.creationDefaults !== null
      ? (settings.creationDefaults as Record<string, unknown>)
      : {}
  // Пустое поле убирает значение: действует умолчание службы; пустой набор убирается из настроек целиком.
  const setDefault = (key: string, value: string) => {
    const entries = Object.entries(defaults).filter(([name]) => name !== key)
    if (value.trim() !== '') entries.push([key, value.trim()])
    if (entries.length === 0) {
      const rest: Record<string, unknown> = { ...settings }
      delete rest.creationDefaults
      onChange(rest)
    } else set('creationDefaults', Object.fromEntries(entries))
  }
  const profile = stringField(settings, 'configurationProfile') || 'small-business'

  return (
    <div className="onec-connection-form">
      <Field
        label="Адрес OData"
        type="url"
        value={stringField(settings, 'baseUrl')}
        onChange={(value) => set('baseUrl', value.trim())}
        // Platform сравнивает адрес с каноническим видом: приводим его, пока человек видит поле.
        onBlur={() => {
          const value = stringField(settings, 'baseUrl')
          const canonical = canonicalUrl(value)
          if (canonical !== null && canonical !== value && canonical.startsWith('https://')) set('baseUrl', canonical)
        }}
        disabled={disabled}
        issue={issueOf('baseUrl')}
        hint="https://onec.example.ru/shop/odata/standard.odata"
      />
      <Field
        label="Информационная база"
        value={stringField(settings, 'infobase')}
        onChange={(value) => set('infobase', value)}
        disabled={disabled}
        issue={issueOf('infobase')}
      />
      <Field
        label="Пользователь 1С"
        value={stringField(settings, 'username')}
        onChange={(value) => set('username', value)}
        disabled={disabled}
        issue={issueOf('username')}
      />
      <SelectField
        label="Конфигурация"
        value={profile}
        options={ONEC_PROFILES}
        onChange={(value) => set('configurationProfile', value)}
      />
      <SelectField label="Часовой пояс базы (для общей аналитики чеков)" value={stringField(settings, 'timeZoneId') || 'Europe/Moscow'}
        options={[{ value: 'Europe/Moscow', label: 'Москва · UTC+3' }, { value: 'Asia/Yekaterinburg', label: 'Екатеринбург · UTC+5' }, { value: 'UTC', label: 'UTC' }]}
        onChange={(value) => set('timeZoneId', value)} disabled={disabled} />
      <CheckboxField
        label="Шаблон создания товаров"
        checked={template !== null}
        onChange={(on) => {
          if (on) set('creationTemplate', template ?? {})
          else {
            const rest: Record<string, unknown> = { ...settings }
            delete rest.creationTemplate
            onChange(rest)
          }
        }}
      />
      {template !== null &&
        ONEC_TEMPLATE_KEYS.map(({ key, label }) => (
          <Field
            key={key}
            label={label}
            value={stringField(template, key)}
            onChange={(value) => setTemplate(key, value.trim())}
            disabled={disabled}
            issue={issueOf(`creationTemplate.${key}`)}
          />
        ))}
      {template !== null &&
        ONEC_CREATION_DEFAULTS.map(({ key, label, hint }) => (
          <Field
            key={key}
            label={label}
            value={stringField(defaults, key)}
            onChange={(value) => setDefault(key, value)}
            disabled={disabled}
            issue={issueOf(`creationDefaults.${key}`)}
            hint={hint}
          />
        ))}
    </div>
  )
}
